import { Button, FormControl, makeStyles, TextField } from '@material-ui/core';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Autocomplete } from '@material-ui/lab';

import { yupResolver } from '@hookform/resolvers/yup';
import { Material, ProductionFilters } from 'types';
import { colors } from 'theme';
import { useMaterials } from 'hooks';
import DatePicker from 'components/CustomInputs/DatePicker';
import NextIcon from 'components/Icons/NextIcon';
import { atEndOfDay, atStartOfDay } from 'utils/date';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { DEFAULT_ITEMS_PER_SELECT, TableFilterContext } from 'tableFilterContext';
import { dateFilterBarSchema } from 'components/Generic/DateFilterBar.yup';

const useStyles = makeStyles({
  fieldLine: {
    marginTop: 8,
    margin: '1vw',
    display: 'block',

    '& span': {
      fontSize: 18,
      margin: 5,
    },
  },
  datePicker: {
    '& label': {
      color: '#949FAC',
    },
    '& > div': {
      marginLeft: '25px',
      width: 150,
    },
    '&:last-of-type > div': {
      marginRight: '25px',
    },
  },
  submitButton: {
    marginTop: 24,
  },
  searchButton: {
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  searchButtonIcon: {
    marginLeft: '10px',
    width: 17,
    height: 17,
  },
  textField: {
    '& fieldset': {
      borderColor: colors.neutralGray,
    },
  },
  autocomplete: {
    width: 500,
    display: 'inline-block',
  },
  autocompleteControls: {
    top: '2px',
  },
  switch: {
    textAlign: 'right',
    color: colors.inputGrey,
  },
});

type ProductionFiltersEncoded = {
  startDate: number | null;
  endDate: number | null;
  material: string | null;
};

type ProductionFilterBarProps = {
  onChange: (filter: ProductionFilters) => void;
  minStartDate: Date;
  maxEndDate: Date;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
};

export const ProductionFilterBar: React.FC<ProductionFilterBarProps> = ({ onChange, minStartDate, maxEndDate }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'productions.filter' });
  const [isReady, setReady] = useState<boolean>(false);
  const { dispatch, search } = useContext(TableFilterContext);
  const [defaultMaterialValue, setDefaultMaterialValue] = useState<Material | null>(null);

  const [minEndDate, setMinEndDate] = useState<Date | null>(minStartDate);
  const [maxStartDate, setMaxStartDate] = useState<Date | null>(maxEndDate);

  const materials = useMaterials({ itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT) });

  const schema = useMemo(
    () => dateFilterBarSchema(t, atStartOfDay(minStartDate), atEndOfDay(maxEndDate)),
    [t, minStartDate, maxEndDate],
  );

  const { control, trigger, handleSubmit, reset } = useForm<ProductionFilters>({
    mode: 'onBlur',
    defaultValues: {
      startDate: null,
      endDate: null,
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (materials === null) return;
    if (!search) {
      setReady(true);
      return;
    }
    const query: ProductionFiltersEncoded = JSON.parse(atob(search));
    if (query.material !== null) {
      setDefaultMaterialValue(materials.find(material => transformObjectId(material) === query.material) ?? null);
    }
    setReady(true);
  }, [search, setReady, materials, setDefaultMaterialValue]);

  useEffect(() => {
    if (!search) return;
    const query: ProductionFiltersEncoded = JSON.parse(atob(search));
    const filter = {
      startDate: query.startDate ? new Date(query.startDate) : null,
      endDate: query.endDate ? new Date(query.endDate) : null,
      material: query.material,
    };
    reset(filter);
    onChange(filter);
  }, [search, reset, onChange]);

  const saveSearchParams = useCallback(
    (e: ProductionFilters) => {
      dispatch({
        type: 'set_search',
        payload: btoa(
          JSON.stringify({
            startDate: e.startDate ? e.startDate.getTime() : null,
            endDate: e.endDate ? e.endDate.getTime() : null,
            material: e.material ?? null,
          }),
        ),
      });
    },
    [dispatch],
  );

  const submitPeriod = useCallback(
    (e: ProductionFilters) => {
      onChange(e);
      saveSearchParams(e);
    },
    [onChange, saveSearchParams],
  );

  return (
    <form onSubmit={handleSubmit(submitPeriod)}>
      <div className={classes.fieldLine}>
        {isReady && (
          <Controller
            name="material"
            control={control}
            render={({ field: { ref, onChange, ...fieldProps } }) => (
              <Autocomplete
                size="small"
                options={materials ?? []}
                getOptionLabel={material => material.shortName}
                defaultValue={defaultMaterialValue}
                classes={{ root: classes.autocomplete, endAdornment: classes.autocompleteControls }}
                noOptionsText={t('noResults')}
                onChange={(_e, value: Material | null) => {
                  onChange({ target: { value: transformObjectId(value) } });
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    className={classes.textField}
                    {...fieldProps}
                    placeholder={pt('material')}
                    variant="outlined"
                  />
                )}
              />
            )}
          />
        )}
        <span className={classes.datePicker}>
          <Controller
            name="startDate"
            control={control}
            render={({ field: { ref, onChange, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="startDate"
                  data-qa-id="start-date-filter"
                  label={t('startDate')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  InputLabelProps={{ required: false }}
                  inputVariant="standard"
                  maxDate={maxStartDate}
                  minDate={minStartDate}
                  onAccept={() => {
                    trigger('startDate');
                  }}
                  onChange={e => {
                    onChange(e);
                    if (!e || (!!e && e < minStartDate)) {
                      setMinEndDate(minStartDate);
                      return;
                    }
                    setMinEndDate(e);
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
        </span>
        <span className={classes.datePicker}>
          <Controller
            name="endDate"
            control={control}
            render={({ field: { ref, onChange, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="endDate"
                  data-qa-id="end-date-filter"
                  label={t('endDate')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  maxDate={maxEndDate}
                  minDate={minEndDate}
                  inputRef={ref}
                  inputVariant="standard"
                  InputLabelProps={{ required: false }}
                  onAccept={() => {
                    trigger('endDate');
                  }}
                  onChange={e => {
                    onChange(e);
                    if (!e || (!!e && e > maxEndDate)) {
                      setMaxStartDate(maxEndDate);
                      return;
                    }
                    setMaxStartDate(e);
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
        </span>
        <Button style={{ marginTop: 12 }} size="small" type="submit" variant="text">
          <div className={classes.searchButton}>{t('search')}</div>
          <NextIcon className={classes.searchButtonIcon} />
        </Button>
      </div>
    </form>
  );
};
